/**
 * Sync Engine with Conflict Resolution
 * Server Wins for financial data, Client Wins for clinical notes
 */

import OfflineService from './OfflineService';

const CONFLICT_RESOLUTION_RULES = {
  // Financial data - Server Wins
  'participant_budget': 'server',
  'shadow_ledger': 'server',
  'claims': 'server',
  'billing': 'server',
  'payment': 'server',
  
  // Clinical data - Client Wins
  'clinical_notes': 'client',
  'medication_administration': 'client',
  'incident_reports': 'client',
  'anacc_assessment': 'client',
  'restrictive_practices': 'client',
  
  // Service delivery - Server Wins (financial implications)
  'service_delivery': 'server',
  
  // Default - Server Wins
  'default': 'server'
};

/**
 * Resolve conflict between local and server data
 * @param {string} resourceType - Resource type
 * @param {object} localData - Local data
 * @param {object} serverData - Server data
 * @returns {object} Resolved data and conflict info
 */
export const resolveConflict = (resourceType, localData, serverData) => {
  const resolutionRule = CONFLICT_RESOLUTION_RULES[resourceType] || CONFLICT_RESOLUTION_RULES.default;
  
  let resolvedData;
  let conflictInfo = {
    hasConflict: true,
    resourceType,
    resolutionRule,
    localTimestamp: localData.updatedAt || localData.createdAt,
    serverTimestamp: serverData.updatedAt || serverData.createdAt,
    resolvedAt: new Date().toISOString()
  };
  
  if (resolutionRule === 'server') {
    resolvedData = { ...serverData };
    conflictInfo.winner = 'server';
    conflictInfo.reason = 'Server wins for financial data';
  } else if (resolutionRule === 'client') {
    resolvedData = { ...localData };
    conflictInfo.winner = 'client';
    conflictInfo.reason = 'Client wins for clinical notes';
  } else {
    // Merge strategy (for some resource types)
    resolvedData = {
      ...serverData,
      ...localData,
      updatedAt: new Date().toISOString(),
      merged: true
    };
    conflictInfo.winner = 'merged';
    conflictInfo.reason = 'Data merged';
  }
  
  return {
    resolvedData,
    conflictInfo
  };
};

/**
 * Sync data with server
 * @param {string} resourceType - Resource type
 * @param {object} localData - Local data
 * @param {function} serverSyncFunction - Function to sync with server
 * @returns {object} Sync result
 */
export const syncWithServer = async (resourceType, localData, serverSyncFunction) => {
  if (!OfflineService.isOnline()) {
    // Cache for later sync
    OfflineService.cacheData(resourceType, localData);
    return {
      success: false,
      cached: true,
      message: 'Device offline - data cached for sync'
    };
  }
  
  try {
    // Attempt server sync
    const serverResult = await serverSyncFunction(localData);
    
    if (serverResult.success) {
      // Check for conflicts
      if (serverResult.conflict) {
        const resolution = resolveConflict(resourceType, localData, serverResult.serverData);
        return {
          success: true,
          synced: true,
          conflict: true,
          resolution: resolution.conflictInfo,
          data: resolution.resolvedData
        };
      }
      
      return {
        success: true,
        synced: true,
        data: serverResult.data
      };
    } else {
      // Sync failed - cache for retry
      OfflineService.cacheData(resourceType, localData);
      return {
        success: false,
        cached: true,
        error: serverResult.error
      };
    }
  } catch (error) {
    // Error - cache for retry
    OfflineService.cacheData(resourceType, localData);
    return {
      success: false,
      cached: true,
      error: error.message
    };
  }
};

/**
 * Batch sync multiple resources
 * @param {array} resources - Array of {resourceType, data, syncFunction}
 * @returns {object} Batch sync result
 */
export const batchSync = async (resources) => {
  const results = [];
  
  for (const resource of resources) {
    const result = await syncWithServer(
      resource.resourceType,
      resource.data,
      resource.syncFunction
    );
    results.push({
      resourceType: resource.resourceType,
      ...result
    });
  }
  
  return {
    success: results.every(r => r.success),
    total: results.length,
    succeeded: results.filter(r => r.success).length,
    failed: results.filter(r => !r.success).length,
    cached: results.filter(r => r.cached).length,
    conflicts: results.filter(r => r.conflict).length,
    results
  };
};

export default {
  resolveConflict,
  syncWithServer,
  batchSync,
  CONFLICT_RESOLUTION_RULES
};

